import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Stack, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, IconButton, Tooltip, Divider, Alert, Checkbox, Avatar,
  InputBase, Select, MenuItem, Card, LinearProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  HourglassTopRounded, PaymentsRounded, CategoryRounded, GroupsRounded,
  OpenInNewRounded, RefreshRounded, SearchRounded, CloseRounded,
  TaskAltRounded, SpeedRounded, DoneAllRounded, BoltRounded, InboxRounded,
} from '@mui/icons-material';
import { approvalService } from '../../services/workspaceService';
import { useAuth } from '../../context/AuthContext';
import { useModules } from '../../context/ModulesContext';
import AssistantPanel, { useAssistantAvailable } from '../../ai/AssistantPanel';
import ExportButton from '../../components/common/ExportButton';
import { ErrorState, StatusDot, TypePill, StatusTone } from '../../components/common/pageKit';
import { formatIndianNumber, formatCurrency } from '../../utils/currency';
import {
  PageHero, MetricTile, SectionHeading, AiPanel, AiBadge, EmptyState, LoadingState, IconTile,
} from '../../design/primitives';
import { COLOR, GRADIENT, RADIUS, EASE, reducedMotionSx, TYPE } from '../../design/tokens';

/**
 * Approvals Inbox — the cross-module view onto the generic erp-api approval
 * engine (approval_requests / approval_actions, see controllers/approvals.js).
 * "My Queue" uses GET /approvals/pending, scoped to the caller by user + role,
 * and returns every module's pending request they can act on: expense,
 * purchase_requisition, purchase_order, purchase_invoice, vendor_onboarding,
 * treasury_payment, opportunity, discount. The header approval bell, the rail
 * badge and the dashboard widget all link here.
 *
 * Page archetype: APPROVAL (Constitution §14F, §25) — a decision queue, not a
 * table. The hierarchy is Decision Queue (focal) → supporting intelligence →
 * action, per §48.1.
 *
 * Everything on screen is derived from what the API actually returns. There is
 * deliberately no "Priority" column: the engine has no priority field, and a
 * guessed one would be a business signal the system cannot stand behind.
 * AGEING is shown instead — it is real, it is computed from created_at, and it
 * is what actually makes an approval urgent.
 */

type ApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';

interface ApprovalRequestRow {
  id: number;
  entity_type: string;
  entity_id: number;
  title: string;
  amount: number | string | null;
  status: ApprovalStatus;
  current_step: number;
  requested_by: number;
  requested_by_name?: string | null;
  requester_name?: string | null;
  completed_at?: string | null;
  created_at: string;
}

interface ApprovalAction {
  id: number;
  step_number: number;
  approver_id: number;
  approver_name?: string | null;
  action: 'APPROVED' | 'REJECTED' | 'DELEGATED' | 'COMMENTED';
  comments?: string | null;
  created_at: string;
}

interface ApprovalDetail {
  request: ApprovalRequestRow;
  actions: ApprovalAction[];
}

const TYPE_TINT: Record<string, string> = {
  expense: '#d97706',
  purchase_requisition: '#0284c7',
  purchase_order: '#2563eb',
  purchase_invoice: '#7c3aed',
  vendor_onboarding: '#0891b2',
  treasury_payment: '#059669',
  opportunity: '#0284c7',
  discount: '#7c3aed',
};

const typeLabel = (t: string): string => (t || '').replace(/_/g, ' ').toUpperCase();
const typeTitle = (t: string): string =>
  (t || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/** entity_type → the record's own detail route, when one exists. Best-effort:
 * types without a per-record page fall back to their module list, and unknown
 * types get no link (the dialog still lets you approve/reject). */
const detailRoute = (entityType: string, entityId: number): string | null => {
  switch (entityType) {
    case 'purchase_order': return `/procurement/orders/${entityId}`;
    case 'purchase_invoice': return `/procurement/invoices/${entityId}`;
    case 'purchase_requisition': return `/procurement/requisitions/${entityId}`;
    case 'expense': return `/finance/expenses/${entityId}`;
    case 'opportunity': return `/crm/opportunity/${entityId}/workspace`;
    case 'vendor_onboarding': return `/procurement/vendor-onboarding`;
    case 'treasury_payment': return `/treasury/payments`;
    default: return null;
  }
};

const STATUS_TONE: Record<ApprovalStatus, StatusTone> = {
  PENDING: 'warning', APPROVED: 'success', REJECTED: 'error', CANCELLED: 'neutral',
};

const DAY_MS = 86_400_000;

const fmtDate = (v?: string | null): string => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const fmtTime = (v?: string | null): string => {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};
const fmtDateTime = (v?: string | null): string => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const requesterLabel = (row: ApprovalRequestRow): string =>
  row.requested_by_name || row.requester_name || (row.requested_by ? `User #${row.requested_by}` : '—');

const initialsOf = (name: string): string =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('') || '?';

/** Whole days a request has been waiting. Real, and the only urgency signal
 *  the approval engine can actually support. */
const ageDays = (row: ApprovalRequestRow): number =>
  Math.max(0, Math.floor((Date.now() - new Date(row.created_at).getTime()) / DAY_MS));

/** Ageing bands. Not a stored priority — a presentation of elapsed time. */
const ageTone = (d: number): { color: string; label: string } => {
  if (d >= 5) return { color: COLOR.danger, label: 'Overdue' };
  if (d >= 3) return { color: COLOR.warning, label: 'Ageing' };
  return { color: COLOR.inkMuted, label: 'Fresh' };
};

const MoneyText: React.FC<{ value: number | string | null | undefined }> = ({ value }) => {
  const n = value === null || value === undefined || value === '' ? null : Number(value);
  if (n === null || Number.isNaN(n)) return <Typography variant="body2" color="text.disabled">—</Typography>;
  return (
    <Typography variant="body2" title={formatCurrency(n)} sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
      {formatIndianNumber(n)}
    </Typography>
  );
};

// ───────────────────────────────────────────────────────────────────────────
// Queue tab strip — My Queue / All Requests / per-type, each with a live count
// ───────────────────────────────────────────────────────────────────────────
const QueueTabs: React.FC<{
  tab: string;
  onChange: (v: string) => void;
  queueCount: number;
  allCount: number;
  byType: Array<[string, number]>;
}> = ({ tab, onChange, queueCount, allCount, byType }) => {
  const items: Array<{ key: string; label: string; count: number }> = [
    { key: 'QUEUE', label: 'My Queue', count: queueCount },
    { key: 'ALL', label: 'All Requests', count: allCount },
    ...byType.map(([t, c]) => ({ key: `TYPE:${t}`, label: typeTitle(t), count: c })),
  ];

  return (
    <Box
      role="tablist"
      aria-label="Approval queues"
      sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2 }}
    >
      {items.map((it) => {
        const on = tab === it.key;
        return (
          <Box
            key={it.key}
            role="tab"
            tabIndex={0}
            aria-selected={on}
            onClick={() => onChange(it.key)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onChange(it.key);
              }
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.875,
              px: 1.75,
              py: 0.875,
              borderRadius: `${RADIUS.button}px`,
              cursor: 'pointer',
              fontSize: '0.8125rem',
              fontWeight: 700,
              whiteSpace: 'nowrap',
              border: '1px solid',
              transition: `background .2s ${EASE}, color .2s ${EASE}`,
              ...reducedMotionSx,
              ...(on
                ? { background: GRADIENT.ramp, color: '#fff', borderColor: 'transparent' }
                : { color: 'text.secondary', borderColor: 'divider', backgroundColor: 'background.paper' }),
              '&:focus-visible': { outline: `2px solid ${COLOR.primary}`, outlineOffset: 2 },
            }}
          >
            {it.label}
            <Box
              component="span"
              sx={{
                px: 0.75,
                borderRadius: `${RADIUS.pill}px`,
                fontSize: '0.6875rem',
                fontWeight: 800,
                fontVariantNumeric: 'tabular-nums',
                backgroundColor: on ? 'rgba(255,255,255,0.24)' : (t) => alpha(t.palette.text.primary, 0.07),
              }}
            >
              {it.count}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

// ───────────────────────────────────────────────────────────────────────────
// Page
// ───────────────────────────────────────────────────────────────────────────
const ApprovalsInboxPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isModuleEnabled } = useModules();
  const assistantAvailable = useAssistantAvailable();

  const [queueRows, setQueueRows] = useState<ApprovalRequestRow[]>([]);
  const [allRows, setAllRows] = useState<ApprovalRequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<string>('QUEUE');

  // Toolbar state — all client-side over what the API already returned.
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | ApprovalStatus>('ALL');
  const [sort, setSort] = useState<'NEWEST' | 'OLDEST' | 'AMOUNT_DESC' | 'AGE_DESC'>('NEWEST');

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkDone, setBulkDone] = useState(0);
  const [assistantOpen, setAssistantOpen] = useState(false);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<ApprovalDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [acting, setActing] = useState<'APPROVE' | 'REJECT' | null>(null);

  /**
   * Both datasets are fetched together. The queue drives the decision list;
   * the full request history is what makes the insight rail possible (average
   * approval time needs completed requests, which /pending never returns).
   */
  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    setSelected(new Set());
    Promise.allSettled([approvalService.pending(), approvalService.requests({})])
      .then(([pendingRes, allRes]) => {
        if (pendingRes.status === 'fulfilled') {
          const res: any = pendingRes.value;
          const merged: ApprovalRequestRow[] = ((res?.groups as any[]) || []).flatMap(
            (g) => (g?.requests as ApprovalRequestRow[]) || []
          );
          merged.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
          setQueueRows(merged);
        }
        if (allRes.status === 'fulfilled') {
          const res: any = allRes.value;
          const list: ApprovalRequestRow[] = (res?.requests as ApprovalRequestRow[]) || [];
          list.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
          setAllRows(list);
        }
        // Only a total failure is an error — the queue alone is still useful.
        if (pendingRes.status === 'rejected' && allRes.status === 'rejected') {
          setError((pendingRes.reason as any)?.message || 'Failed to load approvals.');
        }
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const pendingRows = useMemo(() => queueRows.filter((r) => r.status === 'PENDING'), [queueRows]);

  const pendingValue = useMemo(
    () => pendingRows.reduce((s, r) => s + (r.amount == null ? 0 : Number(r.amount) || 0), 0),
    [pendingRows]
  );

  const byType = useMemo(() => {
    const m = new Map<string, number>();
    pendingRows.forEach((r) => m.set(r.entity_type, (m.get(r.entity_type) || 0) + 1));
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
  }, [pendingRows]);

  const requesterCount = useMemo(
    () => new Set(pendingRows.map((r) => r.requested_by)).size,
    [pendingRows]
  );

  /** Insight metrics, all computed from the full request history. */
  const insights = useMemo(() => {
    const approved = allRows.filter((r) => r.status === 'APPROVED');
    const rejected = allRows.filter((r) => r.status === 'REJECTED');
    const cycles = approved
      .filter((r) => r.completed_at)
      .map((r) => (new Date(r.completed_at as string).getTime() - new Date(r.created_at).getTime()) / DAY_MS)
      .filter((d) => Number.isFinite(d) && d >= 0);
    const avgDays = cycles.length ? cycles.reduce((s, d) => s + d, 0) / cycles.length : null;
    return {
      avgDays,
      received: allRows.length,
      approved: approved.length,
      rejected: rejected.length,
      // Share of decided requests that were approved — a real rate, not a
      // percentage of everything (pending requests have no outcome yet).
      approvalRate:
        approved.length + rejected.length > 0
          ? Math.round((approved.length / (approved.length + rejected.length)) * 100)
          : null,
    };
  }, [allRows]);

  /** The rows the table shows, after tab → search → status → sort. */
  const visibleRows = useMemo(() => {
    let base: ApprovalRequestRow[];
    if (tab === 'ALL') base = allRows;
    else if (tab.startsWith('TYPE:')) base = pendingRows.filter((r) => r.entity_type === tab.slice(5));
    else base = pendingRows;

    const q = query.trim().toLowerCase();
    let list = base.filter((r) => {
      if (statusFilter !== 'ALL' && r.status !== statusFilter) return false;
      if (!q) return true;
      return (
        r.title?.toLowerCase().includes(q) ||
        requesterLabel(r).toLowerCase().includes(q) ||
        typeLabel(r.entity_type).toLowerCase().includes(q)
      );
    });

    const t = (r: ApprovalRequestRow) => new Date(r.created_at).getTime();
    if (sort === 'NEWEST') list = [...list].sort((a, b) => t(b) - t(a));
    else if (sort === 'OLDEST' || sort === 'AGE_DESC') list = [...list].sort((a, b) => t(a) - t(b));
    else if (sort === 'AMOUNT_DESC')
      list = [...list].sort((a, b) => (Number(b.amount) || 0) - (Number(a.amount) || 0));
    return list;
  }, [tab, allRows, pendingRows, query, statusFilter, sort]);

  /** Only PENDING rows can be acted on, so only those are selectable. */
  const selectableIds = useMemo(
    () => visibleRows.filter((r) => r.status === 'PENDING').map((r) => r.id),
    [visibleRows]
  );
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const someSelected = selectableIds.some((id) => selected.has(id)) && !allSelected;

  const toggleAll = () => {
    setSelected((prev) => {
      if (selectableIds.every((id) => prev.has(id))) {
        const next = new Set(prev);
        selectableIds.forEach((id) => next.delete(id));
        return next;
      }
      return new Set([...Array.from(prev), ...selectableIds]);
    });
  };
  const toggleOne = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  /**
   * Bulk approve. Sequential rather than parallel on purpose: each approval
   * posts a ledger-affecting decision through the engine's own step machine,
   * and firing twenty at once invites lock contention on approval_actions for
   * no user-visible gain.
   */
  const bulkApprove = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBulkRunning(true);
    setBulkDone(0);
    setError(null);
    let failed = 0;
    for (const id of ids) {
      try {
        await approvalService.approve(id);
      } catch {
        failed += 1;
      }
      setBulkDone((n) => n + 1);
    }
    setBulkRunning(false);
    if (failed > 0) setError(`${failed} of ${ids.length} approvals could not be completed.`);
    load();
  };

  const openDetail = (id: number) => {
    setSelectedId(id);
    setComment('');
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    approvalService.detail(id)
      .then((res: any) => setDetail(res))
      .catch((e: any) => setDetailError(e?.message || 'Failed to load this request.'))
      .finally(() => setDetailLoading(false));
  };
  const closeDetail = () => {
    setSelectedId(null); setDetail(null); setDetailError(null); setComment(''); setActing(null);
  };
  const act = (action: 'APPROVE' | 'REJECT') => {
    if (selectedId === null) return;
    setActing(action);
    setDetailError(null);
    const call = action === 'APPROVE' ? approvalService.approve : approvalService.reject;
    call(selectedId, comment.trim() || undefined)
      .then(() => { closeDetail(); load(); })
      .catch((e: any) => { setDetailError(e?.message || `Failed to ${action.toLowerCase()} this request.`); setActing(null); });
  };
  const openRecord = (entityType: string, entityId: number) => {
    const r = detailRoute(entityType, entityId);
    if (r) navigate(r);
  };

  const detailRequest = detail?.request;
  const canAct = detailRequest?.status === 'PENDING';

  // The automation call-out points at a real, reachable screen or it is not
  // rendered — an admin-only, module-gated route.
  const canAutomate =
    ['SUPER_ADMIN', 'ADMIN'].includes((user?.role || '').toUpperCase()) && isModuleEnabled('workflows');

  const exportRows = visibleRows.map((r) => ({
    request: r.title,
    module: typeLabel(r.entity_type),
    amount: r.amount == null ? '' : Number(r.amount),
    requested_by: requesterLabel(r),
    status: r.status,
    ageing_days: ageDays(r),
    created_at: fmtDateTime(r.created_at),
  }));

  return (
    <Box>
      <PageHero
        crumbs={[{ label: 'Home', to: '/dashboard' }, { label: 'Approvals' }]}
        title="Approvals Inbox"
        description="Everything that needs your decision, in one place — expenses, requisitions, purchase orders and invoices, vendor onboarding and treasury payments."
        lockup={{ line1: 'Faster decisions.', line2: 'Smoother operations.' }}
        actions={
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={load} disabled={loading} aria-label="Refresh approvals">
                <RefreshRounded />
              </IconButton>
            </span>
          </Tooltip>
        }
      >
        {/* Metrics live inside the hero band so the page has one focal region
            rather than a header followed by a detached row of cards (§48.1). */}
        <Box
          sx={{
            position: 'relative',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
            gap: 2,
            px: { xs: 2.5, md: 4 },
            pb: 3,
          }}
        >
          <MetricTile
            label="Awaiting your approval"
            value={loading ? null : pendingRows.length}
            icon={<HourglassTopRounded />}
            tone="warning"
            hint={pendingRows.length ? 'Needs a decision' : 'Queue is clear'}
          />
          <MetricTile
            label="Pending value"
            value={loading ? null : formatIndianNumber(pendingValue)}
            icon={<PaymentsRounded />}
            tone="info"
            hint="Across your queue"
          />
          <MetricTile
            label="Request types"
            value={loading ? null : byType.length}
            icon={<CategoryRounded />}
            tone="success"
            hint={byType.slice(0, 3).map(([t]) => typeTitle(t)).join(', ') || '—'}
          />
          <MetricTile
            label="Requesters"
            value={loading ? null : requesterCount}
            icon={<GroupsRounded />}
            tone="neutral"
            hint="Waiting on you"
          />
        </Box>
      </PageHero>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: `${RADIUS.card}px` }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1fr) 340px' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        {/* ── LEFT: the decision queue — the focal region of this page ───── */}
        <Box sx={{ minWidth: 0 }}>
          <QueueTabs
            tab={tab}
            onChange={(v) => { setTab(v); setSelected(new Set()); }}
            queueCount={pendingRows.length}
            allCount={allRows.length}
            byType={byType}
          />

          {/* Toolbar */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              flexWrap: 'wrap',
              mb: 2,
              p: 1.25,
              borderRadius: `${RADIUS.card}px`,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
            }}
          >
            <Box
              sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                flex: '1 1 220px', minWidth: 180, px: 1.5, py: 0.75,
                borderRadius: `${RADIUS.input}px`,
                border: '1px solid', borderColor: 'divider',
                backgroundColor: (t) => alpha(t.palette.text.primary, 0.03),
                '&:focus-within': { borderColor: COLOR.primary },
              }}
            >
              <SearchRounded sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
              <InputBase
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search requests, requesters…"
                inputProps={{ 'aria-label': 'Search approval requests' }}
                sx={{ flexGrow: 1, fontSize: '0.8125rem' }}
              />
              {query && (
                <CloseRounded
                  role="button" tabIndex={0} aria-label="Clear search"
                  onClick={() => setQuery('')}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setQuery(''); } }}
                  sx={{ fontSize: 17, color: 'text.secondary', cursor: 'pointer' }}
                />
              )}
            </Box>

            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              size="small"
              inputProps={{ 'aria-label': 'Filter by status' }}
              sx={{ minWidth: 132, borderRadius: `${RADIUS.input}px`, fontSize: '0.8125rem', fontWeight: 600 }}
            >
              <MenuItem value="ALL" sx={{ fontSize: '0.8125rem' }}>All statuses</MenuItem>
              <MenuItem value="PENDING" sx={{ fontSize: '0.8125rem' }}>Pending</MenuItem>
              <MenuItem value="APPROVED" sx={{ fontSize: '0.8125rem' }}>Approved</MenuItem>
              <MenuItem value="REJECTED" sx={{ fontSize: '0.8125rem' }}>Rejected</MenuItem>
              <MenuItem value="CANCELLED" sx={{ fontSize: '0.8125rem' }}>Cancelled</MenuItem>
            </Select>

            <Select
              value={sort}
              onChange={(e) => setSort(e.target.value as any)}
              size="small"
              inputProps={{ 'aria-label': 'Sort requests' }}
              sx={{ minWidth: 150, borderRadius: `${RADIUS.input}px`, fontSize: '0.8125rem', fontWeight: 600 }}
            >
              <MenuItem value="NEWEST" sx={{ fontSize: '0.8125rem' }}>Newest first</MenuItem>
              <MenuItem value="AGE_DESC" sx={{ fontSize: '0.8125rem' }}>Longest waiting</MenuItem>
              <MenuItem value="AMOUNT_DESC" sx={{ fontSize: '0.8125rem' }}>Highest value</MenuItem>
              <MenuItem value="OLDEST" sx={{ fontSize: '0.8125rem' }}>Oldest first</MenuItem>
            </Select>

            <Box sx={{ flexGrow: 1 }} />
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {visibleRows.length} shown
            </Typography>
            <ExportButton
              rows={exportRows}
              columns={[
                { header: 'Request', key: 'request' },
                { header: 'Module', key: 'module' },
                { header: 'Amount', key: 'amount' },
                { header: 'Requested by', key: 'requested_by' },
                { header: 'Status', key: 'status' },
                { header: 'Ageing (days)', key: 'ageing_days' },
                { header: 'Created', key: 'created_at' },
              ]}
              filename="approvals"
            />
          </Box>

          {/* Bulk action bar — only while a selection exists. */}
          {selected.size > 0 && (
            <Box
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
                mb: 2, px: 2, py: 1.5,
                borderRadius: `${RADIUS.card}px`,
                color: '#fff', background: GRADIENT.ramp,
              }}
            >
              <DoneAllRounded sx={{ fontSize: 20 }} />
              <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', flexGrow: 1 }}>
                {selected.size} request{selected.size > 1 ? 's' : ''} selected
                {bulkRunning ? ` · approving ${bulkDone}/${selected.size}…` : ''}
              </Typography>
              <Button
                size="small" variant="contained" disableElevation disabled={bulkRunning}
                onClick={bulkApprove}
                sx={{ textTransform: 'none', fontWeight: 700, backgroundColor: '#fff', color: COLOR.primary, '&:hover': { backgroundColor: '#E0E7FF' } }}
              >
                Approve selected
              </Button>
              <Button
                size="small" variant="text" disabled={bulkRunning}
                onClick={() => setSelected(new Set())}
                sx={{ textTransform: 'none', fontWeight: 700, color: '#fff' }}
              >
                Clear
              </Button>
            </Box>
          )}
          {bulkRunning && (
            <LinearProgress
              variant="determinate"
              value={selected.size ? (bulkDone / selected.size) * 100 : 0}
              sx={{ mb: 2, borderRadius: 2 }}
            />
          )}

          {/* Queue */}
          <Card
            elevation={0}
            sx={{ borderRadius: `${RADIUS.container}px`, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}
          >
            {loading ? (
              <LoadingState message="Stratify is gathering requests awaiting your decision…" rows={5} />
            ) : error && visibleRows.length === 0 ? (
              <ErrorState message={error} onRetry={load} />
            ) : visibleRows.length === 0 ? (
              <EmptyState
                icon={<InboxRounded />}
                title={tab === 'QUEUE' ? 'Nothing awaiting your approval' : 'No requests match this view'}
                body={
                  tab === 'QUEUE'
                    ? 'Your decision queue is clear. New requests from any module will appear here the moment they are raised.'
                    : 'Try a different search term, status, or switch back to My Queue.'
                }
                primaryAction={
                  tab !== 'QUEUE'
                    ? { label: 'Back to my queue', onClick: () => { setTab('QUEUE'); setQuery(''); setStatusFilter('ALL'); } }
                    : undefined
                }
              />
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ borderBottomColor: 'divider' }}>
                        <Checkbox
                          size="small"
                          checked={allSelected}
                          indeterminate={someSelected}
                          onChange={toggleAll}
                          disabled={selectableIds.length === 0}
                          inputProps={{ 'aria-label': 'Select all pending requests' }}
                        />
                      </TableCell>
                      {['Request', 'Module', 'Amount', 'Requested by', 'Created', 'Ageing', 'Status', ''].map((h, i) => (
                        <TableCell
                          key={h || `a${i}`}
                          align={h === 'Amount' ? 'right' : 'left'}
                          sx={{
                            ...TYPE.eyebrow,
                            color: 'text.secondary',
                            py: 1.25,
                            whiteSpace: 'nowrap',
                            backgroundColor: (t) => alpha(t.palette.text.primary, 0.02),
                          }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleRows.map((row) => {
                      const route = detailRoute(row.entity_type, row.entity_id);
                      const name = requesterLabel(row);
                      const d = ageDays(row);
                      const age = ageTone(d);
                      const isPending = row.status === 'PENDING';
                      return (
                        <TableRow
                          key={row.id}
                          hover
                          onClick={() => openDetail(row.id)}
                          sx={{
                            cursor: 'pointer',
                            '& td': { borderBottomColor: 'divider', py: 1.25 },
                            // Row actions appear on hover (§15) rather than
                            // occupying a permanent column of buttons.
                            '& .rowActions': { opacity: 0, transition: `opacity .15s ${EASE}` },
                            '&:hover .rowActions, &:focus-within .rowActions': { opacity: 1 },
                            ...reducedMotionSx,
                          }}
                        >
                          <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              size="small"
                              checked={selected.has(row.id)}
                              disabled={!isPending}
                              onChange={() => toggleOne(row.id)}
                              inputProps={{ 'aria-label': `Select ${row.title}` }}
                            />
                          </TableCell>

                          <TableCell sx={{ maxWidth: 300 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                              <IconTile size={30} color={TYPE_TINT[row.entity_type] || COLOR.inkMuted}>
                                <TaskAltRounded />
                              </IconTile>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                  title={row.title}
                                >
                                  {row.title}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Step {row.current_step} · #{row.id}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>

                          <TableCell>
                            <TypePill label={typeLabel(row.entity_type)} tint={TYPE_TINT[row.entity_type] || '#475569'} />
                          </TableCell>

                          <TableCell align="right"><MoneyText value={row.amount} /></TableCell>

                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar
                                sx={{
                                  width: 28, height: 28, fontSize: '0.6875rem', fontWeight: 700,
                                  color: '#fff', background: GRADIENT.ramp,
                                }}
                              >
                                {initialsOf(name)}
                              </Avatar>
                              <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                                {name}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{fmtDate(row.created_at)}</Typography>
                            <Typography variant="caption" color="text.secondary">{fmtTime(row.created_at)}</Typography>
                          </TableCell>

                          <TableCell>
                            <Tooltip title={`Waiting ${d} day${d === 1 ? '' : 's'} · ${age.label}`}>
                              <Box
                                sx={{
                                  display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                  px: 0.875, py: 0.25,
                                  borderRadius: `${RADIUS.pill}px`,
                                  fontSize: '0.6875rem', fontWeight: 800, whiteSpace: 'nowrap',
                                  color: age.color,
                                  backgroundColor: alpha(age.color, 0.12),
                                }}
                              >
                                {d}d
                              </Box>
                            </Tooltip>
                          </TableCell>

                          <TableCell>
                            <StatusDot label={row.status} tone={STATUS_TONE[row.status] || 'neutral'} />
                          </TableCell>

                          <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                            <Box className="rowActions" sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                              <Button
                                size="small" variant="outlined"
                                onClick={() => openDetail(row.id)}
                                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: `${RADIUS.button}px`, px: 1.5 }}
                              >
                                Review
                              </Button>
                              {route && (
                                <Tooltip title="Open source record">
                                  <IconButton size="small" onClick={() => navigate(route)} aria-label="Open source record">
                                    <OpenInNewRounded fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Card>

          {/* Automation call-out — real destination or not rendered at all. */}
          {canAutomate && !loading && (
            <Card
              elevation={0}
              sx={{
                display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
                mt: 2.5, p: 2.25,
                borderRadius: `${RADIUS.container}px`,
                border: '1px solid', borderColor: 'divider',
                backgroundColor: (t) => alpha(COLOR.primary, t.palette.mode === 'dark' ? 0.12 : 0.045),
              }}
            >
              <IconTile size={40} tone="ramp"><BoltRounded /></IconTile>
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>
                  Route these decisions automatically
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Set approval thresholds, delegation and escalation rules so routine requests never reach your queue.
                </Typography>
              </Box>
              <Button
                variant="contained" disableElevation
                onClick={() => navigate('/workflows')}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: `${RADIUS.button}px`, background: GRADIENT.ramp }}
              >
                Configure workflows
              </Button>
            </Card>
          )}
        </Box>

        {/* ── RIGHT: supporting intelligence ─────────────────────────────── */}
        <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {assistantAvailable && (
            <AiPanel
              body="Your decision assistant for approvals. Ask about a request, a policy, or the impact of approving it."
              cta="Ask about a request…"
              onOpen={() => setAssistantOpen(true)}
            />
          )}

          <Card
            elevation={0}
            sx={{ p: 2.5, borderRadius: `${RADIUS.container}px`, border: '1px solid', borderColor: 'divider' }}
          >
            <SectionHeading icon={<SpeedRounded />} title="Approval insights" />

            {loading ? (
              <LoadingState message="Analysing approval history…" rows={3} />
            ) : insights.received === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No approval history yet. Metrics appear once requests have been decided.
              </Typography>
            ) : (
              <Stack divider={<Divider flexItem />} spacing={0}>
                <InsightRow
                  label="Average approval time"
                  value={insights.avgDays === null ? '—' : `${insights.avgDays.toFixed(1)} days`}
                  hint={insights.avgDays === null ? 'No completed approvals yet' : 'Across completed requests'}
                />
                <InsightRow label="Requests received" value={insights.received} hint="All time" />
                <InsightRow label="Approved" value={insights.approved} tone={COLOR.success} />
                <InsightRow label="Rejected" value={insights.rejected} tone={COLOR.danger} />
                {insights.approvalRate !== null && (
                  <InsightRow
                    label="Approval rate"
                    value={`${insights.approvalRate}%`}
                    hint="Of decided requests"
                  />
                )}
              </Stack>
            )}
          </Card>

          {/* Queue composition — the same byType data the tabs use, read as a
              distribution rather than as a filter. */}
          {!loading && byType.length > 0 && (
            <Card
              elevation={0}
              sx={{ p: 2.5, borderRadius: `${RADIUS.container}px`, border: '1px solid', borderColor: 'divider' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.75 }}>
                <Typography sx={{ ...TYPE.sectionTitle, fontSize: '0.9375rem', flexGrow: 1 }}>
                  What's waiting
                </Typography>
                <AiBadge label="Live" />
              </Box>
              {byType.map(([t, c]) => {
                const pct = pendingRows.length ? (c / pendingRows.length) * 100 : 0;
                const tint = TYPE_TINT[t] || COLOR.inkMuted;
                return (
                  <Box key={t} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1, minWidth: 0 }} noWrap>
                        {typeTitle(t)}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                        {c}
                      </Typography>
                    </Box>
                    <Box sx={{ height: 6, borderRadius: 3, backgroundColor: (th) => alpha(th.palette.text.primary, 0.06) }}>
                      <Box sx={{ width: `${pct}%`, height: '100%', borderRadius: 3, backgroundColor: tint }} />
                    </Box>
                  </Box>
                );
              })}
            </Card>
          )}
        </Box>
      </Box>

      {/* Review dialog — unchanged behaviour, restyled surface. */}
      <Dialog
        open={selectedId !== null}
        onClose={closeDetail}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: `${RADIUS.dialog}px` } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Review request</DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={26} /></Box>
          ) : detailError && !detailRequest ? (
            <ErrorState message={detailError} onRetry={selectedId !== null ? () => openDetail(selectedId) : undefined} />
          ) : detailRequest ? (
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                  {detailRequest.title}
                </Typography>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 0.75 }}>
                  <TypePill label={typeLabel(detailRequest.entity_type)} tint={TYPE_TINT[detailRequest.entity_type] || '#475569'} />
                  <StatusDot label={detailRequest.status} tone={STATUS_TONE[detailRequest.status] || 'neutral'} />
                </Stack>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Field label="Amount"><MoneyText value={detailRequest.amount} /></Field>
                <Field label="Requested by">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{requesterLabel(detailRequest)}</Typography>
                </Field>
                <Field label="Current step">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{detailRequest.current_step}</Typography>
                </Field>
                <Field label="Waiting">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {ageDays(detailRequest)} day{ageDays(detailRequest) === 1 ? '' : 's'}
                  </Typography>
                </Field>
                <Field label="Created">
                  <Typography variant="body2">{fmtDateTime(detailRequest.created_at)}</Typography>
                </Field>
                {detailRequest.completed_at && (
                  <Field label="Completed">
                    <Typography variant="body2">{fmtDateTime(detailRequest.completed_at)}</Typography>
                  </Field>
                )}
              </Box>

              {detailRoute(detailRequest.entity_type, detailRequest.entity_id) && (
                <Button
                  size="small" variant="outlined" startIcon={<OpenInNewRounded fontSize="small" />}
                  onClick={() => openRecord(detailRequest.entity_type, detailRequest.entity_id)}
                  sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 700, borderRadius: `${RADIUS.button}px` }}
                >
                  Open source record
                </Button>
              )}

              {(detail?.actions?.length || 0) > 0 && (
                <Box>
                  <Divider sx={{ mb: 1.5 }} />
                  <Typography sx={{ ...TYPE.eyebrow, color: 'text.secondary', mb: 1 }}>Audit trail</Typography>
                  <Stack spacing={1.25}>
                    {detail!.actions.map((a) => (
                      <Box key={a.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {a.action} — {a.approver_name || `User #${a.approver_id}`}
                          </Typography>
                          {a.comments && (
                            <Typography variant="caption" color="text.secondary">{a.comments}</Typography>
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          {fmtDateTime(a.created_at)}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {canAct && (
                <TextField
                  label="Comments (optional)" multiline minRows={2} value={comment}
                  onChange={(e) => setComment(e.target.value)} fullWidth
                />
              )}
              {detailError && <Alert severity="error">{detailError}</Alert>}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeDetail} sx={{ textTransform: 'none', fontWeight: 600 }}>Close</Button>
          {canAct && (
            <>
              <Button
                color="error" variant="outlined" disabled={acting !== null} onClick={() => act('REJECT')}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: `${RADIUS.button}px` }}
              >
                {acting === 'REJECT' ? <CircularProgress size={18} /> : 'Reject'}
              </Button>
              <Button
                variant="contained" disableElevation disabled={acting !== null} onClick={() => act('APPROVE')}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: `${RADIUS.button}px`, background: GRADIENT.ramp }}
              >
                {acting === 'APPROVE' ? <CircularProgress size={18} color="inherit" /> : 'Approve'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {assistantAvailable && (
        <AssistantPanel open={assistantOpen} onClose={() => setAssistantOpen(false)} />
      )}
    </Box>
  );
};

/** Labelled value pair used inside the review dialog. */
const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
    {children}
  </Box>
);

/** One row of the insight rail. */
const InsightRow: React.FC<{
  label: string;
  value: React.ReactNode;
  hint?: string;
  tone?: string;
}> = ({ label, value, hint, tone }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25 }}>
    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
      {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
    </Box>
    <Typography
      sx={{
        fontWeight: 800,
        fontSize: '1.0625rem',
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
        color: tone || 'text.primary',
      }}
    >
      {value}
    </Typography>
  </Box>
);

export default ApprovalsInboxPage;
